import parseTemplate, {
  attributesToProps,
  DOMNode,
  domToReact,
  HTMLReactParserOptions,
  Element,
} from 'html-react-parser'
import { kebabToCamelObject } from '@skedulo/breeze-ui-react'
import { PlatformComponent } from '../../core/components'
import { EventBusScope } from '../../core/context'
import { PlatformErrorBoundary } from '../../errors/components'
import { PlatformErrorBoundaryProxyAttributes } from '../../errors/types'
import { ViewStateScope } from '../../view-state/context'
import { HorizonContainer } from '../components/horizon-container'
import { Anchor, Link } from '../components/link'
import { HORIZON_SCOPE } from '../constants'

const validatePlatformComponentAttributes = (attribs: {
  [attribName: string]: string
}): attribs is {
  [attribName: string]: string
  'package-name': string
  name: string
} => {
  if (!attribs['package-name'] || !attribs['name']) {
    return false
  }
  return true
}

const isElement = (domNode: DOMNode): domNode is Element => {
  return domNode.type === 'tag' && (domNode as Element).attribs !== undefined
}

const reactParserOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (!isElement(domNode)) {
      return
    }

    switch (domNode.name) {
      case 'platform-component': {
        if (!validatePlatformComponentAttributes(domNode.attribs)) {
          throw Error(
            '<platform-component> should have package-name and name attributes',
          )
        }

        const { key, ...restProps } = kebabToCamelObject(domNode.attribs)

        const children = domToReact(domNode.children, reactParserOptions)

        // Key has to be added as a prop without spreading otherwise React throws a warning
        return key ? (
          <PlatformComponent {...restProps} key={key}>
            {children}
          </PlatformComponent>
        ) : (
          <PlatformComponent {...restProps}>{children}</PlatformComponent>
        )
      }

      /**
       * <platform-error-boundary> is a proxy component to hook template content
       * into a React ErrorBoundary. This will not capture errors thrown by the
       * Nunjucks environment, which are handled separately as template errors.
       * This will capture errors thrown by Platform Components it wraps around.
       */
      case 'platform-error-boundary': {
        const { level, message } = kebabToCamelObject(
          domNode.attribs,
        ) as PlatformErrorBoundaryProxyAttributes

        return (
          <PlatformErrorBoundary level={level} message={message}>
            {domToReact(domNode.children, reactParserOptions)}
          </PlatformErrorBoundary>
        )
      }

      case 'platform-eventbus-scope': {
        const { allowedEvents } = kebabToCamelObject(domNode.attribs)

        return (
          <EventBusScope
            closed={'closed' in domNode.attribs}
            allowedEvents={allowedEvents}
          >
            {domToReact(domNode.children, reactParserOptions)}
          </EventBusScope>
        )
      }

      case 'horizon-view-state-scope': {
        const { viewType, scope, scopeId } = kebabToCamelObject(domNode.attribs)

        return (
          // scopeId should be undefined rather than an empty string.
          <ViewStateScope
            viewType={viewType}
            scope={scope}
            scopeId={scopeId ? scopeId : undefined}
          >
            {domToReact(domNode.children, reactParserOptions)}
          </ViewStateScope>
        )
      }

      case 'brz-link': {
        return (
          <Link {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children, reactParserOptions)}
          </Link>
        )
      }

      case 'a': {
        // Intercept links so that the `routing:navigate:v1` event can
        // be triggered instead of navigating
        return (
          <Anchor {...attributesToProps(domNode.attribs)}>
            {domToReact(domNode.children, reactParserOptions)}
          </Anchor>
        )
      }

      default: {
        // Look for custom Horizon components and wrap them in a container for
        // registration
        if (domNode.name.startsWith(`${HORIZON_SCOPE}-`)) {
          const props = kebabToCamelObject(attributesToProps(domNode.attribs))
          const { key, ...restProps } = props

          const componentProps = {
            tagName: domNode.name,
            component: domToReact([domNode]),
            ...restProps,
          }
          const children = domToReact(domNode.children, reactParserOptions)

          // Key has to be added as a prop without spreading otherwise React throws a warning
          return key ? (
            <HorizonContainer {...componentProps} key={key}>
              {children}
            </HorizonContainer>
          ) : (
            <HorizonContainer {...componentProps}>{children}</HorizonContainer>
          )
        }
      }
    }
  },
}

export const parsePlatformTemplate = (templateString: string) =>
  parseTemplate(templateString, reactParserOptions)
